import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { AuthError } from 'aws-amplify/auth';
import { type Href, Redirect, useLocalSearchParams, useRouter } from 'expo-router';
import { useAtomValue } from 'jotai';
import { AlertCircleIcon, EyeIcon, EyeOffIcon } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';
import { z } from 'zod';

import { AwarenessLogo } from '@/components/AwarenessLogo';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { NeedHelpLink } from '@/components/NeedHelpLink';
import { NpiConflictInfoBoxLogin } from '@/components/NpiConflictInfoBoxLogin';
import { Header } from '@/components/header';
import { Heading } from '@/components/heading';
import { SupportForm } from '@/components/supportForm';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Input, InputField, InputIcon, InputSlot } from '@/components/ui/input';
import { Link, LinkText } from '@/components/ui/link';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import UnauthenticatedLayout from '@/components/unauthenticatedLayout';
import { SUPPORT_EMAIL_ADDRESS } from '@/constants/links';
import { isPubliclyBrowsableRoute, ROUTES } from '@/constants/routes';
import { FIFTEEN_MINUTES } from '@/constants/time';
import { useAuth } from '@/hooks/useAuth';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { useColors } from '@/hooks/useColors';
import { useCreateAccount } from '@/hooks/useCreateAccount';
import { useRedirectPath } from '@/hooks/useRedirectPath';
import { useSupportForm } from '@/hooks/useSupportForm';
import { useToaster } from '@/hooks/useToaster';
import { clearAccountLinkingContext, loadAccountLinkingContext } from '@/services/accountLinking';
import { getIdToken, startGoogleSignIn } from '@/services/auth';
import { getCurrentUserScopedQueryOptions } from '@/services/getCurrentUserScoped';
import { authStateResolvedAtom, store, userIdAtom } from '@/services/globalState';
import { linkProvider } from '@/services/linkProvider';
import { parseIdTokenClaims } from '@/services/session';
import {
  trackAuthTriggered,
  trackLoginFailed,
  trackLoginSuccess,
  trackAccountLinked,
  trackAccountLinkingFailed,
} from '@/util/analyticsEvents';
import { getLoginFailureToast } from '@/util/authResolveError';
import { isApiErrorV2 } from '@/util/fetchClient';
import { Sentry } from '@/util/sentry';
import { isEmbeddedFrame } from '@/util/smartLaunch';
import { isSameUserId } from '@/utils/isSameUserId';

const ERROR_MESSAGE = `Please try again. If this problem persists, use the forgot password link below or contact support at ${SUPPORT_EMAIL_ADDRESS}`;

/**
 * Login screen content component.
 *
 * Handles password login with account linking support. When account linking context exists,
 * attempts to link OAuth provider to password account after successful login.
 *
 * ACCOUNT LINKING VALIDATION:
 * Validates that the logged-in user matches the account to be linked before and after the
 * linking request to prevent incorrect account linking. Edge cases handled:
 * - User signs in with wrong account: Pre-request validation blocks linking and shows error.
 *   User can still log in normally without linking.
 * - Backend returns wrong user ID: Post-response validation detects mismatch, shows error,
 *   clears context, and allows normal login.
 * - Backend validation errors: Handles MISMATCH, EXPIRED_TOKEN, INVALID_TOKEN (show error,
 *   allow login), ALREADY_LINKED (show info, allow login), and other errors (show generic,
 *   allow login). Linking failures never block login.
 *
 * After successful login or account linking, eagerly resolves user and sets userId before
 * navigation to prevent auth state flicker. Updates React Query cache and invalidates to
 * ensure fresh data is fetched.
 *
 * Account linking context expires after 15 minutes TTL.
 */
function Content() {
  const { navigate, replace, setParams } = useRouter();

  const { reset } = useCreateAccount();

  const toast = useToaster();

  const { redirectPath, appendRedirectPath, navigateToRedirectPath } = useRedirectPath();

  const defaultRedirectPath = '/(secure)/(tabs)/directory';

  const redirectAfterLogin = () => navigateToRedirectPath(defaultRedirectPath);

  const [showPassword, setShowPassword] = useState(false);

  const params = useLocalSearchParams<{ email?: string; provider?: string }>();

  const initialEmail = typeof params.email === 'string' ? params.email.trim().toLowerCase() : '';

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      email: initialEmail,
      password: '',
    },
    resolver: zodResolver(
      z.object({
        email: z.string().min(1, 'Email is required').email({
          message: 'Invalid email address',
        }),
        password: z.string().min(1, 'Password is required'),
      })
    ),
  });

  const passwordRef = useRef<React.ComponentProps<typeof InputField>>(null);

  const { login } = useAuth();

  const [linkingCtx, setLinkingCtx] = useState<Awaited<
    ReturnType<typeof loadAccountLinkingContext>
  > | null>(null);

  useEffect(() => {
    const loadContext = async () => {
      const context = await loadAccountLinkingContext();

      if (context && context.createdAt) {
        const now = Date.now();

        if (now - context.createdAt > FIFTEEN_MINUTES) {
          await clearAccountLinkingContext();

          setLinkingCtx(null);

          return;
        }
      }

      setLinkingCtx(context);
    };

    loadContext().catch(() => setLinkingCtx(null));
  }, []);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await login(data);

      if (result.type === 'newPasswordRequired') {
        return navigate(appendRedirectPath(ROUTES.NEW_PASSWORD));
      }

      if (result.type === 'unsupportedStep') {
        trackLoginFailed(
          new Error(`Unsupported Cognito signInStep: ${result.step}`),
          data.email,
          'login_screen'
        );

        return toast.showError(getLoginFailureToast('unsupportedStep'));
      }

      if (result.type === 'transientFailure') {
        // login() already cleared the Cognito session via signOutLocal — clear any
        // pending linking context so the user starts clean on retry.
        await clearAccountLinkingContext();

        return toast.showError(getLoginFailureToast('transientFailure'));
      }

      if (result.type === 'persistentFailure') {
        await clearAccountLinkingContext();

        return toast.showError(getLoginFailureToast('persistentFailure'));
      }

      // result.type === 'success' — atom and /v2/users/me cache are already set inside login()
      await trackLoginSuccess(result.user, 'login_screen');

      const context = await loadAccountLinkingContext();

      if (context && context.authProvider === 'password') {
        try {
          const currentIdToken = await getIdToken();

          const currentClaims = currentIdToken ? parseIdTokenClaims(currentIdToken) : null;

          const currentUserIdFromToken = currentClaims?.['custom:user_id'];

          const duplicateUserId = context.duplicateUserId;

          const loggedInUserId = result.user.userId;

          const userIdsMatch = isSameUserId(duplicateUserId, loggedInUserId);

          const tokenUserIdMatchesDuplicate = isSameUserId(duplicateUserId, currentUserIdFromToken);

          if (!userIdsMatch && !tokenUserIdMatchesDuplicate) {
            await clearAccountLinkingContext();

            toast.showError({
              title: 'Unable to link accounts',
              message: 'The account you signed in with does not match the account to be linked.',
            });

            redirectAfterLogin();

            return;
          }

          const response = await linkProvider({
            duplicateUserId: context.duplicateUserId,
            duplicateIdToken: context.duplicateIdToken,
          });

          if (response?.success) {
            const linkedUserIdFromResponse = response.user?.id;

            if (
              linkedUserIdFromResponse &&
              !isSameUserId(linkedUserIdFromResponse, duplicateUserId)
            ) {
              await clearAccountLinkingContext();

              toast.showError({
                title: 'Account linking failed',
                message: 'The accounts could not be linked. Please try again or contact support.',
              });

              redirectAfterLogin();

              return;
            }

            const linkedUserId = store.get(userIdAtom);

            trackAccountLinked({
              method: 'manual',
              provider: 'google',
              source: 'login_screen',
              userId: linkedUserId || result.user.userId,
              email: result.user.email,
              linkStatus: 'new',
            });

            await clearAccountLinkingContext();

            redirectAfterLogin();

            return;
          }

          // linkProvider resolved without throwing but reported a non-success state.
          // Surface this so the user is not silently stranded with an unfinished link.
          Sentry.captureMessage('linkProvider returned non-success without error', {
            level: 'warning',
            tags: { component: 'Login', source: 'login_screen' },
          });

          trackAccountLinkingFailed({
            method: 'manual',
            provider: 'google',
            source: 'login_screen',
            error: 'linkProvider returned non-success',
            userId: result.user.userId,
            email: result.user.email,
          });

          toast.showError({
            title: 'Unable to link accounts',
            message: 'Account linking did not complete. Please try again or contact support.',
          });
        } catch (e) {
          trackAccountLinkingFailed({
            method: 'manual',
            provider: 'google',
            source: 'login_screen',
            error: e instanceof Error ? e.message : String(e),
            userId: result.user.userId,
            email: result.user.email,
          });

          if (isApiErrorV2(e)) {
            const body = e.body as {
              error?: string;
              errorCode?: string;
            };

            const errorCode = body?.errorCode;

            if (
              errorCode === 'MISMATCH' ||
              errorCode === 'EXPIRED_TOKEN' ||
              errorCode === 'INVALID_TOKEN'
            ) {
              toast.showError({
                title: "Unable to link accounts. The token doesn't match the expected account.",
              });
            } else if (errorCode === 'ALREADY_LINKED') {
              trackAccountLinked({
                method: 'manual',
                provider: 'google',
                source: 'login_screen',
                userId: result.user.userId,
                email: result.user.email,
                linkStatus: 'linked',
              });

              toast.showInfo({
                title: 'Accounts are already linked',
                message: '',
              });
            } else {
              toast.showError({
                title: 'Unable to link accounts',
                message: body?.error || 'An error occurred while linking accounts.',
              });
            }
          } else {
            toast.showError({
              title: 'Unable to link accounts',
              message: 'An error occurred while linking accounts.',
            });
          }
        }
      }

      redirectAfterLogin();
    } catch (error) {
      if (error instanceof Error) {
        trackLoginFailed(error, data.email, 'login_screen');
      }

      let title = 'Sorry, there was a server error';

      if (error instanceof AuthError && error.name === 'NotAuthorizedException') {
        title = 'Incorrect user name or password';
      }

      toast.showError({
        title,
        message: ERROR_MESSAGE,
      });
    }
  });

  const { colors } = useColors();

  const insets = useSafeAreaInsets();

  const onSignUp = () => {
    reset();

    replace(appendRedirectPath('/create-account/step-1'));
  };

  const handleGoogleSignIn = () => {
    if (linkingCtx?.authProvider === 'password' && linkingCtx?.duplicateUserId) {
      toast.showInfo({
        title: 'Account linking in progress',
        message:
          'Please sign in with your password to complete account linking, or click "Not linking? Log in" to start fresh.',
      });

      return;
    }

    trackAuthTriggered({ provider: 'google', source: 'login_screen' });

    /*
     * Google refuses to show its sign-in inside a frame, so inside an EHR the same login page
     * opens in a top-level tab, where the Google button works.
     */
    if (isEmbeddedFrame()) {
      window.open(window.location.href, '_blank', 'noopener');

      return;
    }

    startGoogleSignIn('login_screen', redirectPath ? { redirectPath } : undefined);
  };

  /*
   * Support hands a Google-only user `/login?provider=google` so one click reaches Google instead
   * of the form. The param is cleared as it fires, so coming back from Google with the browser's
   * back button lands on the plain form rather than starting Google again.
   */
  const startedGoogleFromLink = useRef(false);

  useEffect(() => {
    if (params.provider !== 'google' || startedGoogleFromLink.current) {
      return;
    }

    startedGoogleFromLink.current = true;

    setParams({ provider: undefined });

    handleGoogleSignIn();
  });

  const contactSupport = useSupportForm({
    initialValues: {
      name: '',
      email: watch('email'),
      reason: 'Account Access',
    },
  });

  const goToEmailLink = () => {
    const email = watch('email')?.trim();

    const target = email ? `/email-link?email=${encodeURIComponent(email)}` : '/email-link';

    navigate(appendRedirectPath(target));
  };

  return (
    <VStack
      className={twJoin('flex-1 bg-primary-500 web:bg-transparent')}
      style={{
        paddingTop: insets.top,
      }}>
      <Header
        headerShown={false}
        animation="fade"
        statusBarBackgroundColor={colors.primary[500]}
        statusBarStyle="light"
      />

      <KeyboardAwareScrollView
        mode="layout"
        bottomOffset={12}
        keyboardShouldPersistTaps="handled"
        disableScrollOnKeyboardHide
        keyboardDismissMode="on-drag"
        showsVerticalScrollIndicator={false}
        contentContainerClassName="flex-col flex-grow web:px-0">
        <VStack className="flex-1">
          {!isWeb ? (
            <VStack className="flex-1 items-center justify-start pt-5">
              <AwarenessLogo
                alt="Primum logo"
                kind="light"
                box={{ height: 52, width: 260, marginBottom: 24, alignSelf: 'center' }}
              />
            </VStack>
          ) : null}
          <VStack
            style={{
              paddingTop: isWeb ? insets.top : 32,
              paddingBottom: isWeb ? insets.bottom : 32,
            }}
            className={twJoin(
              'm-3 mb-10 justify-end rounded-b-2xl rounded-t-2xl bg-background-0 px-5 py-8',
              'web:m-0 web:mx-auto web:w-full web:rounded-none web:bg-transparent web:px-0 web:py-0'
            )}>
            {isWeb ? (
              <VStack className="items-start">
                <AwarenessLogo
                  alt="Primum logo with mark"
                  kind="dark2x"
                  box={{ height: 35, width: 280, marginBottom: 24 }}
                />
                <Heading size="h1-xl" className="mb-4 font-medium text-typography-900">
                  {linkingCtx?.authProvider === 'password' && linkingCtx?.duplicateUserId
                    ? 'Link your existing account'
                    : 'Sign in to Primum'}
                </Heading>
              </VStack>
            ) : null}

            {(() => {
              const shouldShowLinkingHint =
                linkingCtx?.authProvider === 'password' && linkingCtx?.duplicateUserId;

              return shouldShowLinkingHint ? (
                <NpiConflictInfoBoxLogin maskedEmail={linkingCtx.maskedEmail} />
              ) : null;
            })()}

            <Controller
              name="email"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl isInvalid={!!errors.email?.message} className="mb-4">
                  <FormControlLabel className="mb-2">
                    <FormControlLabelText>Email*</FormControlLabelText>
                  </FormControlLabel>

                  <Input>
                    <InputField
                      testID="login-email-input"
                      aria-label="Email address"
                      accessibilityLabel="Email address"
                      autoCorrect={false}
                      placeholder="Enter email address"
                      placeholderTextColor={colors.typography[600]}
                      style={{ color: colors.typography[900] }}
                      className="text-base"
                      value={value}
                      onChangeText={(text: string) => onChange(text.toLowerCase())}
                      keyboardType="email-address"
                      autoCapitalize="none"
                      enterKeyHint="next"
                      onSubmitEditing={() => {
                        if (isWeb) {
                          return onSubmit();
                        }

                        // @ts-expect-error
                        passwordRef.current?.focus();
                      }}
                    />
                  </Input>

                  <FormControlError className="mt-2">
                    <FormControlErrorIcon size="xs" as={AlertCircleIcon} />

                    <FormControlErrorText size="xs">{errors.email?.message}</FormControlErrorText>
                  </FormControlError>
                </FormControl>
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl isInvalid={!!errors.password?.message} className="mb-6">
                  <FormControlLabel className="mb-2">
                    <FormControlLabelText>Password*</FormControlLabelText>
                  </FormControlLabel>

                  <Input>
                    <InputField
                      ref={passwordRef}
                      testID="login-password-input"
                      aria-label="Password"
                      accessibilityLabel="Password"
                      autoCapitalize="none"
                      placeholder="Enter password"
                      placeholderTextColor={colors.typography[600]}
                      style={{ color: colors.typography[900] }}
                      className="text-base"
                      value={value}
                      onChangeText={onChange}
                      type={showPassword ? 'text' : 'password'}
                      returnKeyType="go"
                      enterKeyHint="go"
                      onSubmitEditing={onSubmit}
                    />

                    <InputSlot className="pr-3" onPress={() => setShowPassword((prev) => !prev)}>
                      <InputIcon as={showPassword ? EyeIcon : EyeOffIcon} className="" />
                    </InputSlot>
                  </Input>

                  <FormControlError className="mt-2">
                    <FormControlErrorIcon size="xs" as={AlertCircleIcon} />

                    <FormControlErrorText size="xs">
                      {errors.password?.message}
                    </FormControlErrorText>
                  </FormControlError>
                </FormControl>
              )}
            />

            <Button
              testID="login-submit-button"
              className="mb-6 gap-x-2"
              onPress={() => onSubmit()}
              disabled={isSubmitting}>
              {isSubmitting ? <ButtonSpinner color={colors.primary[200]} /> : null}

              <ButtonText>Sign in</ButtonText>
            </Button>

            {isWeb ? (
              <>
                <HStack className="mb-4 flex-row items-center gap-x-2">
                  <Divider className="flex-1" />
                  <Text size="xs" className="text-typography-600">
                    OR
                  </Text>
                  <Divider className="flex-1" />
                </HStack>
                <VStack className="mb-6 gap-3">
                  <Button onPress={goToEmailLink} variant="outline">
                    <ButtonText>Email a sign-in link</ButtonText>
                  </Button>
                </VStack>
                {!linkingCtx?.authProvider && (
                  <>
                    <HStack className="mb-4 flex-row items-center gap-x-2">
                      <Divider className="flex-1" />

                      <Text size="xs" className="text-typography-600">
                        OR
                      </Text>

                      <Divider className="flex-1" />
                    </HStack>

                    <VStack className="mb-6 gap-3">
                      <GoogleSignInButton onPress={handleGoogleSignIn} />
                    </VStack>
                  </>
                )}

                <HStack className="mb-6 justify-center gap-x-1">
                  <Text size="sm" className="font-normal text-typography-900">
                    Don't have an account?
                  </Text>

                  <Link onPress={onSignUp}>
                    <LinkText
                      size="sm"
                      className="font-bold text-primary-500 data-[hover=true]:text-primary-600">
                      Sign up for free
                    </LinkText>
                  </Link>
                </HStack>

                <HStack space="sm" className="items-center justify-center">
                  <NeedHelpLink onPress={contactSupport.onOpen} />
                  <Text className="text-primary">•</Text>
                  <Link onPress={() => navigate(appendRedirectPath('/forgot-password'))}>
                    <LinkText
                      size="md"
                      className="text-primary-500 data-[hover=true]:text-primary-600">
                      Forgot Password
                    </LinkText>
                  </Link>
                </HStack>
              </>
            ) : (
              <>
                <HStack className="mb-4 flex-row items-center gap-x-2">
                  <Divider className="flex-1" />
                  <Text size="xs" className="text-typography-600">
                    OR
                  </Text>
                  <Divider className="flex-1" />
                </HStack>
                <VStack className="mb-4 gap-3">
                  <Button onPress={goToEmailLink} variant="outline">
                    <ButtonText>Email a sign-in link</ButtonText>
                  </Button>
                </VStack>
                {!linkingCtx?.authProvider && (
                  <>
                    <HStack className="mb-4 flex-row items-center gap-x-2">
                      <Divider className="flex-1" />

                      <Text size="xs" className="text-typography-600">
                        OR
                      </Text>

                      <Divider className="flex-1" />
                    </HStack>

                    <VStack className="mb-4 gap-3">
                      <GoogleSignInButton onPress={handleGoogleSignIn} />
                    </VStack>
                  </>
                )}

                <HStack
                  space="sm"
                  className="mb-3 items-center justify-center web:mb-10 web:justify-start">
                  <NeedHelpLink onPress={contactSupport.onOpen} />
                  <Text className="text-primary">•</Text>
                  <Link onPress={() => navigate(appendRedirectPath('/forgot-password'))}>
                    <LinkText
                      size="md"
                      className="text-primary-500 data-[hover=true]:text-primary-600">
                      Forgot Password
                    </LinkText>
                  </Link>
                </HStack>

                <HStack className="mt-3 justify-start gap-x-1 web:mt-6">
                  <Text size="sm" className="font-normal text-typography-900">
                    Don't have an account?
                  </Text>

                  <Link onPress={onSignUp}>
                    <LinkText
                      size="sm"
                      className="font-bold text-primary-500 data-[hover=true]:text-primary-600">
                      Sign up for free
                    </LinkText>
                  </Link>
                </HStack>
              </>
            )}
          </VStack>
          <SupportForm
            isOpen={contactSupport.isOpen}
            onClose={contactSupport.onClose}
            initialValues={contactSupport.initialValues}
          />
        </VStack>
      </KeyboardAwareScrollView>
    </VStack>
  );
}

/**
 * Login page component.
 *
 * Redirects authenticated users to directory. During account linking flows, authenticated
 * users remain on login to complete the linking process. Returns null while auth state
 * resolves to prevent flash of login screen.
 */
export default function Login() {
  const { redirectPath } = useRedirectPath();

  /*
   * Backing out of sign-in has to land somewhere a signed-out visitor can actually see.
   * A destination that needs an account is what sent them here in the first place, so
   * going back to it would bounce them straight back to this screen.
   */
  const backDestination = (
    redirectPath && isPubliclyBrowsableRoute(redirectPath) ? redirectPath : ROUTES.DIRECTORY
  ) as Href;

  const { navigateBack } = useBackNavigation(backDestination);

  const userId = useAtomValue(userIdAtom);

  const authStateResolved = useAtomValue(authStateResolvedAtom);

  const { isLoading } = useQuery(getCurrentUserScopedQueryOptions({ userId }));

  const [hasLinkingContext, setHasLinkingContext] = useState<boolean | null>(null);

  useEffect(() => {
    const checkLinkingContext = async () => {
      try {
        const context = await loadAccountLinkingContext();

        setHasLinkingContext(!!context);
      } catch {
        setHasLinkingContext(false);
      }
    };

    checkLinkingContext();
  }, []);

  if (!authStateResolved || (!userId && isLoading)) {
    return null;
  }

  if (!userId) {
    return isWeb ? (
      <UnauthenticatedLayout onBack={navigateBack}>
        <Content />
      </UnauthenticatedLayout>
    ) : (
      <Content />
    );
  }

  if (hasLinkingContext === null) {
    return null;
  }

  if (userId && hasLinkingContext) {
    return (
      <VStack className="flex-1 items-center justify-center gap-6 p-6">
        <Spinner size="large" />
        <Text className="text-center font-semibold text-xl">Linking your account…</Text>
      </VStack>
    );
  }

  if (!hasLinkingContext) {
    return <Redirect href={ROUTES.DIRECTORY_TAB} />;
  }

  return isWeb ? (
    <UnauthenticatedLayout onBack={navigateBack}>
      <Content />
    </UnauthenticatedLayout>
  ) : (
    <Content />
  );
}
